import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Gift as GiftIcon } from 'lucide-react';

/**
 * GiftPicker — modal showing the catalog of free cultural gifts.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSend: (gift: {id, emoji, label_fr, color}) => void
 */
const CATEGORY_LABELS = {
  culture: 'Culture',
  music: 'Musique',
  cinema: 'Cinéma',
  reaction: 'Réactions',
};

const GiftPicker = ({ open, onClose, onSend }) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!open || catalog.length > 0) return;
    setLoading(true);
    axios.get('/api/gifts/catalog')
      .then(r => setCatalog(r.data?.gifts || []))
      .catch(() => setCatalog([]))
      .finally(() => setLoading(false));
  }, [open, catalog.length]);

  if (!open) return null;

  const cats = ['all', ...new Set(catalog.map(g => g.category))];
  const visible = filter === 'all' ? catalog : catalog.filter(g => g.category === filter);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-6"
         onClick={onClose} data-testid="gift-picker-overlay">
      <div className="w-full max-w-md bg-gradient-to-br from-gray-900 to-gray-950 border border-white/10 rounded-3xl shadow-2xl overflow-hidden flex flex-col"
           onClick={(e) => e.stopPropagation()}
           style={{ maxHeight: 'min(85vh, calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 16px))' }}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <GiftIcon className="w-5 h-5 text-fuchsia-300" />
            Envoyer un cadeau
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" aria-label="Fermer">
            <X className="w-5 h-5 text-gray-300" />
          </button>
        </div>
        <div className="px-3 py-2 border-b border-white/5 overflow-x-auto shrink-0">
          <div className="flex gap-1.5 min-w-min">
            {cats.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                  filter === c ? 'bg-fuchsia-600 text-white' : 'bg-white/5 text-gray-300 hover:bg-white/10'
                }`}
                data-testid={`gift-cat-${c}`}
              >
                {c === 'all' ? 'Tout' : CATEGORY_LABELS[c] || c}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3 overflow-y-auto flex-1 overscroll-contain">
          {loading ? (
            <p className="text-center text-gray-400 py-6 text-sm">Chargement…</p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {visible.map(g => (
                <button
                  key={g.id}
                  onClick={() => onSend(g)}
                  className="flex flex-col items-center gap-1 p-3 rounded-2xl bg-white/5 hover:bg-fuchsia-500/15 hover:scale-105 transition-all"
                  data-testid={`gift-${g.id}`}
                >
                  <span className="text-3xl" aria-hidden>{g.emoji}</span>
                  <span className="text-[10px] text-gray-300 text-center leading-tight">{g.label_fr}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <p
          className="px-4 py-2 text-[10px] text-center text-gray-500 border-t border-white/5 shrink-0"
          style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
        >
          Tous les cadeaux sont gratuits 💝
        </p>
      </div>
    </div>
  );
};

export default GiftPicker;
